import * as Integrations from '@sentry/integrations'
import * as Sentry from '@sentry/react'
import { scrubEvent } from '@sleepstation/utils'
import { pipe, prop, test } from 'ramda'
import { FIELDS_TO_SCRUB_BY_API } from '../const/errorTracking'
import SUPPORTED_BROWSERS_REGEXP from '../const/supportedBrowsersRegex'
import { isProductionBuild } from '../helpers/environment'
import { getRelease } from '../helpers/sentry'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const SERIALIZE_DEPTH = 10

const TIMEOUT_MESSAGE_REGEXP = /^Timeout(?:\s\(h\))?$/

const release = getRelease()

// Check to see if the user-agent is amongst our list of supported browsers
const isSupportedBrowser = test(SUPPORTED_BROWSERS_REGEXP, navigator.userAgent)

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const testForRecaptchaTimeout = pipe(
  prop(`originalException`),
  test(TIMEOUT_MESSAGE_REGEXP)
)

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

const initializeErrorReporter = ({ isEnabled = true }) => {
  // We use Sentry to log exceptions. This provides configurations.
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    environment: process.env.ACTIVE_ENV || `development`,
    // Add additional data about the error to the report
    integrations: [
      new Integrations.ExtraErrorData({ depth: SERIALIZE_DEPTH }),
      new Integrations.CaptureConsole({ levels: [`error`] }),
      new Sentry.BrowserTracing(),
    ],
    // Filter out exceptions that originate in code that is loaded from a URL
    // that doesn't match this Regex
    allowUrls: [
      /https:\/\/wellbeing(-(alpha(1|2|3)|staging))?.sleepstation\.org\.uk/,
    ],
    // Set the current release
    release,
    enabled: isEnabled,
    // Scrub sensitive data from events before uploading
    beforeSend(event, hint) {
      if (
        // Don't log exceptions from unsupported browsers
        !isSupportedBrowser ||
        // Don't log harmless error from reCaptcha being logged.
        // See: https://github.com/getsentry/sentry-javascript/issues/2514
        testForRecaptchaTimeout(hint)
      ) {
        return null
      }
      // Otherwise scrub the event
      return scrubEvent(FIELDS_TO_SCRUB_BY_API)(event)
    },
    // Sample 100% of errors
    sampleRate: 1,
    // Performance sampling:
    tracesSampleRate: isProductionBuild() ? 0.2 : 1.0,
    tracePropagationTargets: [
      /^https:\/\/.*\.sleepstation.org\.uk/,
      /^https:\/\/pedr\.sleepstation\.test/,
    ],

    // Fix bug with serialisation of extra data
    // See: https://github.com/getsentry/sentry-javascript/issues/1964
    normalizeDepth: SERIALIZE_DEPTH + 1,
    //  Uncomment to enable debugging to the console
    debug: true,
  })
}

export default initializeErrorReporter
